import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Option } from 'carbon-react/lib/components/select';
import SimpleSelect, { SimpleSelectProps } from 'carbon-react/lib/components/select/simple-select';

import { ForecastFormElementIds } from '../../types';
import { NewForecastDataSource } from '../../../dashboard/types';

export type ForecastDatasourceProps = Omit<SimpleSelectProps, 'name' | 'id' | 'children' | 'inputHint' | 'label'> & {
  type: 'budget' | 'forecast';
};

export const ForecastDatasource = ({ type, ...props }: ForecastDatasourceProps) => {
  const intl = useIntl();

  const [label, inputHint] = useMemo(() => {
    if (type === 'budget') {
      return [
        intl.formatMessage({
          id: 'predict.create_forecast.add_predictions',
          defaultMessage: 'Add predictions'
        }),
        intl.formatMessage({
          id: 'predict.create_forecast.you_can_edit_and_add_to_your_budget_later',
          defaultMessage: 'You can edit and add to your budget later'
        })
      ];
    } else {
      return [
        intl.formatMessage({
          id: 'predict.create_forecast.add_predictions',
          defaultMessage: 'Add predictions'
        }),
        intl.formatMessage({
          id: 'predict.create_forecast.you_can_edit_and_add_to_your_forecast_later',
          defaultMessage: 'You can edit and add to your forecast later'
        })
      ];
    }
  }, [intl, type]);

  return (
    <SimpleSelect
      name={ForecastFormElementIds.Datasource}
      id={ForecastFormElementIds.Datasource}
      label={label}
      inputHint={inputHint}
      required
      listMaxHeight={240}
      {...props}
    >
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.use_predictions_based_on_last_years_actuals_2',
          defaultMessage: "Use predictions based on last year's actuals"
        })}
        value={NewForecastDataSource.FromLYA}
      />
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.use_futrlis_automated_predictions',
          defaultMessage: "Use Futrli's automated predictions"
        })}
        value={NewForecastDataSource.WithAutoPredictions}
      />
      {type === 'budget' ? (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.copy_predictions_from_and_exiting_budget_v2',
            defaultMessage: 'Copy predictions from an existing budget or forecast'
          })}
          value={NewForecastDataSource.FromDuplicate}
        />
      ) : (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.copy_predictions_from_and_exiting_budget_v2',
            defaultMessage: 'Copy predictions from an existing budget or forecast'
          })}
          value={NewForecastDataSource.FromDuplicate}
        />
      )}
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.upload_predictions_from_an_excel_spreadsheet',
          defaultMessage: 'Upload predictions from an Excel spreadsheet'
        })}
        value={NewForecastDataSource.FromExcelUpload}
      />
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.start_from_scratch_without_predictions',
          defaultMessage: 'Start from scratch without predictions'
        })}
        value={NewForecastDataSource.FromScratch}
      />
    </SimpleSelect>
  );
};
